import { ITEM_TYPES } from '../../../server/constants';
import { trpc } from '../../lib/trpc';
import { Button, FileInput, Space } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';

type AttachmentFormProps = {
  readonly onUploaded?: () => void;
  readonly processId: string;
};

export const AttachmentForm = ({
  onUploaded,
  processId,
}: AttachmentFormProps) => {
  const { mutateAsync: createItem } = trpc.items.create.useMutation();
  const { mutateAsync: createPresignedUpload } =
    trpc.files.createPresignedUpload.useMutation();

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    setError: setFormError,
  } = useForm<{
    file: File | null;
  }>({
    defaultValues: {
      file: null,
    },
  });

  const submitHandler = handleSubmit(async (formData) => {
    const file = formData.file;
    if (!file) {
      setFormError('file', {
        message: 'Please select a file',
        type: 'required',
      });
      return;
    }

    try {
      // Step 1: get presigned URL
      const presignedUpload = await createPresignedUpload({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || 'application/octet-stream',
        processId,
      });

      if (!presignedUpload.ok) {
        throw new Error('Presigned URL creation failed');
      }

      // Step 2: upload the file
      const uploadResponse = await fetch(presignedUpload.presignedUrl, {
        body: file,
        // headers: {
        //   'Content-Length': file.size.toString(),
        //   'Content-Type': file.type || 'application/octet-stream',
        // },
        method: 'PUT',
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      // Step 3: create item
      await createItem({
        content: file.name,
        metadata: {
          key: presignedUpload.key,
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
        },
        processId,
        type: ITEM_TYPES.FILE,
      });

      onUploaded?.();

      // Reset form
      reset();
    } catch (error) {
      setFormError('file', {
        message: error instanceof Error ? error.message : 'Upload failed',
        type: 'server',
      });
    }
  });

  return (
    <form
      noValidate
      onSubmit={submitHandler}
    >
      <Controller
        control={control}
        name="file"
        render={({ field: { onChange, ...rest } }) => (
          <FileInput
            accept="*"
            error={errors?.file?.message?.toString()}
            label="Upload files"
            onChange={(value) => onChange(value)}
            {...rest}
          />
        )}
      />
      <Space h="sm" />
      <Button
        loading={isSubmitting}
        type="submit"
      >
        Upload
      </Button>
    </form>
  );
};
